// src/features/account/ActivatePage.js
import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import apiClient from '../../api/apiClient';

const ActivatePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  // States: valid | activated | expired | resent
  const [status, setStatus] = useState(() => token ? 'valid' : 'expired');
  const [errorMessage, setErrorMessage] = useState(() => token ? '' : 'Invalid activation link. No token provided.');
  const [activating, setActivating] = useState(false);
  const [resending, setResending] = useState(false);

  const handleActivate = async () => {
    if (!token) return;
    
    setErrorMessage('');
    setActivating(true);
    try {
      await apiClient.post(`/agent/activate?token=${encodeURIComponent(token)}`);
      setStatus('activated');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message;
      if (error.response?.status === 410 || errorMsg?.includes('expired')) {
        setStatus('expired');
        setErrorMessage('This activation link has expired. Please request a new one.');
      } else if (error.response?.status === 404 || errorMsg?.includes('invalid')) {
        setStatus('expired');
        setErrorMessage('This activation link is invalid or has already been used.');
      } else {
        // Keep status as 'valid' so user can retry - show error inline
        setErrorMessage(errorMsg || 'Failed to activate account. Please try again.');
      }
    } finally {
      setActivating(false);
    }
  };

  const handleResend = async () => {
    if (!token) return;

    setErrorMessage('');
    setResending(true);
    try {
      await apiClient.post('/agent/resend-activation', { token });
      setStatus('resent');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message;
      setErrorMessage(errorMsg || 'Failed to resend activation email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'var(--dwlf-bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        position: 'relative',
      }}
    >
      {/* Subtle noise texture */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.025,
          pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          zIndex: 0,
        }}
      />

      {/* Logo */}
      <Typography
        component={Link}
        to="/"
        sx={{
          fontFamily: '"Space Grotesk", sans-serif',
          fontSize: '2rem',
          fontWeight: 600,
          color: 'var(--dwlf-accent)',
          textDecoration: 'none',
          mb: 6,
          letterSpacing: '-0.02em',
          position: 'relative',
          zIndex: 1,
        }}
      >
        DWLF
      </Typography>

      <Box
        sx={{
          width: '100%',
          maxWidth: 480,
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
        }}
      >
        {/* Valid Token - Show Disclaimer */}
        {status === 'valid' && (
          <>
            <Typography
              sx={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '1.75rem',
                fontWeight: 500,
                color: 'var(--dwlf-text)',
                mb: 3,
                letterSpacing: '-0.02em',
              }}
            >
              Welcome to DWLF
            </Typography>
            
            <Box
              sx={{
                backgroundColor: 'var(--dwlf-ink-03)',
                border: '1px solid var(--dwlf-ink-08)',
                borderRadius: 0,
                p: 3,
                mb: 4,
                textAlign: 'left',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'var(--dwlf-accent)',
                  mb: 2,
                }}
              >
                Important Disclaimer
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.8rem',
                  color: 'var(--dwlf-ink-60)',
                  lineHeight: 1.7,
                  mb: 2,
                }}
              >
                By activating your account, you acknowledge that:
              </Typography>
              <Box component="ul" sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.8rem',
                color: 'var(--dwlf-ink-60)',
                lineHeight: 1.8,
                pl: 2,
                m: 0,
              }}>
                <li>Trading financial instruments involves substantial risk and may not be suitable for all investors.</li>
                <li>Past performance is not indicative of future results.</li>
                <li>Content provided on this platform is for informational purposes only and does not constitute financial advice.</li>
                <li>You are solely responsible for your trading decisions and any resulting losses.</li>
                <li>You confirm that you are of legal age to participate in trading activities in your jurisdiction.</li>
              </Box>
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.8rem',
                  color: 'var(--dwlf-ink-60)',
                  lineHeight: 1.7,
                  mt: 2,
                }}
              >
                By clicking "I agree — activate my account", you confirm that you have read, understood, and agree to these terms.
              </Typography>
            </Box>

            {errorMessage && (
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.85rem',
                  color: 'var(--dwlf-down)',
                  mb: 3,
                }}
              >
                {errorMessage}
              </Typography>
            )}

            <Button
              onClick={handleActivate}
              disabled={activating}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.8rem',
                fontWeight: 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--dwlf-bg)',
                backgroundColor: 'var(--dwlf-accent)',
                borderRadius: 0,
                py: 1.5,
                px: 6,
                minWidth: 280,
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'var(--dwlf-text)',
                },
                '&:disabled': {
                  backgroundColor: 'var(--dwlf-accent-30)',
                  color: 'rgba(0, 0, 0, 0.5)',
                },
              }}
            >
              {activating ? 'Activating...' : 'I agree — activate my account'}
            </Button>
          </>
        )}

        {/* Resent - Check email for new link */}
        {status === 'resent' && (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'var(--dwlf-accent-10)',
                border: '2px solid var(--dwlf-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <Typography sx={{ fontSize: '2rem', color: 'var(--dwlf-accent)' }}>✓</Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '1.75rem',
                fontWeight: 500,
                color: 'var(--dwlf-text)',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Activation Email Sent
            </Typography>
            <Typography
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.9rem',
                color: 'var(--dwlf-ink-60)',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Check your email for the new activation link.<br />
              The old link is no longer valid.
            </Typography>
            <Button
              component={Link}
              to="/"
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.8rem',
                fontWeight: 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--dwlf-bg)',
                backgroundColor: 'var(--dwlf-accent)',
                borderRadius: 0,
                py: 1.5,
                px: 6,
                textDecoration: 'none',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'var(--dwlf-text)',
                },
              }}
            >
              Back to Home
            </Button>
          </>
        )}

        {/* Activated Successfully */}
        {status === 'activated' && (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'var(--dwlf-up-10)',
                border: '2px solid var(--dwlf-up)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <Typography sx={{ fontSize: '2rem', color: 'var(--dwlf-up)' }}>✓</Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '1.75rem',
                fontWeight: 500,
                color: 'var(--dwlf-text)',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Account activated!
            </Typography>
            <Typography
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.9rem',
                color: 'var(--dwlf-ink-60)',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Your account has been activated successfully.<br />
              You can now access all features.
            </Typography>
            <Button
              component={Link}
              to="/login"
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.8rem',
                fontWeight: 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--dwlf-bg)',
                backgroundColor: 'var(--dwlf-accent)',
                borderRadius: 0,
                py: 1.5,
                px: 6,
                textDecoration: 'none',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'var(--dwlf-text)',
                },
              }}
            >
              Get Started
            </Button>
          </>
        )}

        {/* Expired/Invalid Token */}
        {status === 'expired' && (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'var(--dwlf-down-10)',
                border: '2px solid var(--dwlf-down)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <Typography sx={{ fontSize: '2rem', color: 'var(--dwlf-down)' }}>✕</Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '1.75rem',
                fontWeight: 500,
                color: 'var(--dwlf-text)',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Link Expired
            </Typography>
            <Typography
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.9rem',
                color: 'var(--dwlf-ink-60)',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              {errorMessage}
            </Typography>
            {token ? (
              <Button
                onClick={handleResend}
                disabled={resending}
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--dwlf-accent)',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--dwlf-accent-30)',
                  borderRadius: 0,
                  py: 1.5,
                  px: 4,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'var(--dwlf-accent-10)',
                    borderColor: 'var(--dwlf-accent)',
                  },
                  '&:disabled': {
                    color: 'var(--dwlf-accent-40)',
                    borderColor: 'var(--dwlf-accent-20)',
                  },
                }}
              >
                {resending ? <CircularProgress size={16} sx={{ color: 'var(--dwlf-accent)', mr: 1 }} /> : null}
                {resending ? 'Sending...' : 'Resend Activation Email'}
              </Button>
            ) : (
              <Button
                component={Link}
                to="/register"
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--dwlf-accent)',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--dwlf-accent-30)',
                  borderRadius: 0,
                  py: 1.5,
                  px: 4,
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'var(--dwlf-accent-10)',
                    borderColor: 'var(--dwlf-accent)',
                  },
                }}
              >
                Register New Account
              </Button>
            )}
          </>
        )}
      </Box>

      {/* Footer */}
      <Typography
        sx={{
          position: 'absolute',
          bottom: 32,
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.7rem',
          color: 'var(--dwlf-ink-30)',
          letterSpacing: '0.1em',
        }}
      >
        SIGNAL FROM NOISE
      </Typography>
    </Box>
  );
};

export default ActivatePage;